import { TRIP_TYPE, TRIP_TYPE_CONFIG } from '@ixigo/cab-core/location';
import { getFromLocalStorage, setToLocalStorage } from '@/utils/localStorage';
import type { SearchFormData } from './store/types';

const SEARCH_FORM_STORAGE_KEY = 'cab-recent-search';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	!!value && typeof value === 'object';

/**
 * Save search form data to localStorage
 * @param formData - The search form data to save
 * @returns true if saved successfully, false otherwise
 */
export const saveSearchFormData = (formData: SearchFormData): boolean => {
	return setToLocalStorage(SEARCH_FORM_STORAGE_KEY, formData);
};

/**
 * Validate that a saved blob matches the post-FLIG-22203 schema: explicit
 * tripType + the airport-side field actually tagged `type: 'airport'`.
 * Legacy saves (pre-tripType, or with a Google value on the airport side)
 * fail this check and are treated as no save — user starts fresh.
 */
export const isValidSearchFormData = (
	data: unknown,
): data is SearchFormData => {
	if (!isRecord(data)) return false;

	if (typeof data.dateTime !== 'string') return false;
	if (
		data.tripType !== TRIP_TYPE.AIRPORT_DROP_OFF &&
		data.tripType !== TRIP_TYPE.AIRPORT_PICK_UP
	) {
		return false;
	}
	const { pickup, drop } = data;
	if (!isRecord(pickup) || !isRecord(drop)) return false;

	const { airportField } = TRIP_TYPE_CONFIG[data.tripType];
	const airportSide = airportField === 'pickup' ? pickup : drop;
	const otherSide = airportField === 'pickup' ? drop : pickup;
	return airportSide.type === 'airport' && otherSide.type !== 'airport';
};

/**
 * Load and validate search form data from localStorage
 * @returns The saved search form data or null if not found/invalid
 */
export const loadSearchFormData = (): SearchFormData | null => {
	const data = getFromLocalStorage(SEARCH_FORM_STORAGE_KEY);
	return isValidSearchFormData(data) ? data : null;
};

/**
 * Raw localStorage blob — bypasses validation. Used by the legacy-save
 * migration to recover saves that don't pass the strict post-tripType
 * schema check. Callers must not trust this shape.
 */
export const loadRawSearchFormData = (): unknown => {
	return getFromLocalStorage(SEARCH_FORM_STORAGE_KEY);
};
